'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface Article {
  id: string;
  title: string;
  url: string;
  source: string;
  sourceDomain: string;
  summary: string;
  category: MANACategory;
  relevanceScore: number;
  relevanceReason: string;
  publishedDate: string;
  approved: boolean | null; // null = pending, true = approved, false = rejected
  tags: string[];
}

interface NewsletterDigest {
  id: string;
  title: string;
  issueNumber: number;
  date: string;
  categories: MANACategory[];
  articles: Article[];
  htmlOutput: string;
}

type MANACategory =
  | 'marriage-family' |'history-heritage' |'legacy-profiles' |'youth-young-adults' |'faith-ramadan' |'justice-civic';

// ─── CATEGORY CONFIG ──────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<MANACategory, { label: string; color: string; bg: string; description: string; keywords: string[] }> = {
  'marriage-family': {
    label: 'Marriage & Family',
    color: '#c47a7a',
    bg: 'rgba(196,122,122,0.12)',
    description: 'Strengthening Muslim families and marriages',
    keywords: ['marriage', 'family', 'parenting', 'spouse', 'children', 'home', 'nikah', 'divorce', 'domestic'],
  },
  'history-heritage': {
    label: 'History & Heritage',
    color: '#c9a84c',
    bg: 'rgba(201,168,76,0.12)',
    description: 'African-American Muslim history and cultural heritage',
    keywords: ['history', 'heritage', 'legacy', 'African American', 'Black Muslim', 'civil rights', 'Malcolm X', 'Nation of Islam', 'historical'],
  },
  'legacy-profiles': {
    label: 'Legacy & Profiles',
    color: '#8a8ab8',
    bg: 'rgba(138,138,184,0.12)',
    description: 'Profiles of Muslim leaders and community builders',
    keywords: ['profile', 'leader', 'imam', 'scholar', 'activist', 'community builder', 'interview', 'spotlight', 'biography'],
  },
  'youth-young-adults': {
    label: 'Youth & Young Adults',
    color: '#2d8a87',
    bg: 'rgba(45,138,135,0.12)',
    description: 'Programs and resources for Muslim youth (ages 5–34)',
    keywords: ['youth', 'young adults', 'teen', 'student', 'campus', 'MSA', 'mentorship', 'education', 'college', 'high school'],
  },
  'faith-ramadan': {
    label: 'Faith & Ramadan',
    color: '#D4A82A',
    bg: 'rgba(212,168,42,0.12)',
    description: 'Spiritual development, Islamic practice, and Ramadan',
    keywords: ['faith', 'ramadan', 'prayer', 'quran', 'hadith', 'sunnah', 'spiritual', 'deen', 'iman', 'salah', 'fasting', 'eid'],
  },
  'justice-civic': {
    label: 'Justice & Civic Life',
    color: '#2d7a47',
    bg: 'rgba(45,122,71,0.12)',
    description: 'Justice advocacy, civic engagement, and policy',
    keywords: ['justice', 'civic', 'voting', 'policy', 'advocacy', 'rights', 'discrimination', 'Islamophobia', 'reparations', 'prison reform', 'police'],
  },
};

// ─── MANA SOURCE DOMAINS ──────────────────────────────────────────────────────
// Verified article sources from MANA partner orgs, news outlets, and Islamic publications
// Tuned for marriage, heritage, youth, justice, faith, civic topics

const MANA_ALIGNED_SOURCES = [
  // ── MANA Partner Organizations ──────────────────────────────────────────────
  { domain: 'manajustice.org', name: 'MANA Justice', type: 'partner', categories: ['justice-civic', 'legacy-profiles', 'history-heritage'] },
  { domain: 'muslimamericansociety.org', name: 'Muslim American Society (MAS)', type: 'partner', categories: ['justice-civic', 'youth-young-adults', 'faith-ramadan'] },
  { domain: 'icna.org', name: 'Islamic Circle of North America (ICNA)', type: 'partner', categories: ['justice-civic', 'faith-ramadan', 'youth-young-adults'] },
  { domain: 'zakatfoundation.org', name: 'Zakat Foundation', type: 'partner', categories: ['justice-civic', 'faith-ramadan'] },
  { domain: 'ciogc.org', name: 'Council of Islamic Organizations of Greater Chicago (CIOGC)', type: 'partner', categories: ['justice-civic', 'youth-young-adults', 'history-heritage'] },
  { domain: 'isna.net', name: 'Islamic Society of North America (ISNA)', type: 'partner', categories: ['marriage-family', 'faith-ramadan', 'youth-young-adults'] },

  // ── African-American Muslim Publications ─────────────────────────────────────
  { domain: 'sapelo.org', name: 'Sapelo Square', type: 'publication', categories: ['history-heritage', 'legacy-profiles', 'justice-civic'] },
  { domain: 'blackmuslimpsychology.com', name: 'Black Muslim Psychology', type: 'publication', categories: ['marriage-family', 'youth-young-adults', 'history-heritage'] },

  // ── Islamic Scholarly Publications ───────────────────────────────────────────
  { domain: 'yaqeeninstitute.org', name: 'Yaqeen Institute', type: 'scholarly', categories: ['faith-ramadan', 'history-heritage', 'justice-civic'] },
  { domain: 'zaytuna.edu', name: 'Zaytuna College', type: 'scholarly', categories: ['faith-ramadan', 'legacy-profiles', 'history-heritage'] },
  { domain: 'muslimmatters.org', name: 'Muslim Matters', type: 'news', categories: ['marriage-family', 'youth-young-adults', 'faith-ramadan', 'justice-civic'] },
  { domain: 'aboutislam.net', name: 'About Islam', type: 'news', categories: ['faith-ramadan', 'marriage-family', 'youth-young-adults'] },
  { domain: 'soundvision.com', name: 'Sound Vision', type: 'news', categories: ['youth-young-adults', 'marriage-family', 'faith-ramadan'] },
  { domain: 'ilmfeed.com', name: 'IlmFeed', type: 'news', categories: ['faith-ramadan', 'legacy-profiles', 'history-heritage'] },
  { domain: 'themuslimvibe.com', name: 'The Muslim Vibe', type: 'news', categories: ['youth-young-adults', 'faith-ramadan', 'justice-civic'] },
  { domain: 'muslimlink.ca', name: 'Muslim Link', type: 'news', categories: ['justice-civic', 'youth-young-adults', 'history-heritage'] },
];

// ─── ANTHROPIC EXTRACTION PROMPT (tuned for MANA topics) ─────────────────────
// This prompt is used by the Anthropic-powered article discovery system
// Tuned to identify marriage, heritage, youth, justice, faith, civic topics

const MANA_EXTRACTION_SYSTEM_PROMPT = `You are an expert content curator for MANA (Muslim Alliance in North America), a national organization serving African-American Muslim communities. Your role is to identify and evaluate articles from verified Islamic and community-focused sources that align with MANA's six subscriber interest categories. ## MANA's Six Interest Categories (with extraction signals):

### 1. Marriage & Family (marriage-family)
Keywords: marriage, nikah, divorce, parenting, Muslim family, spouse, children, domestic, household, Islamic marriage, family counseling, co-parenting, Muslim women, gender roles
Extraction signals: articles addressing Muslim family dynamics, marriage guidance, parenting challenges, domestic life

### 2. History & Heritage (history-heritage)  
Keywords: African American Muslim, Black Muslim history, Nation of Islam, Imam WD Mohammed, Malcolm X, slavery, abolition, civil rights, Muslim heritage, African Muslim, historical, legacy, cultural memory
Extraction signals: articles documenting African-American Muslim history, cultural preservation, historical figures, community memory

### 3. Legacy & Profiles (legacy-profiles)
Keywords: imam profile, scholar spotlight, community leader, Muslim activist, biography, interview, legacy, tribute, in memoriam, Muslim professional, changemaker
Extraction signals: profiles of Muslim leaders, scholars, imams, activists, community builders

### 4. Youth & Young Adults (youth-young-adults)
Keywords: Muslim youth, young adult, teen, student, MSA, campus, college, high school, mentorship, youth program, Muslim millennials, Gen Z Muslim, youth engagement, Each One Reach One
Extraction signals: content targeting Muslims ages 5-34, campus life, youth programs, mentorship

### 5. Faith & Ramadan (faith-ramadan)
Keywords: Ramadan, fasting, prayer, salah, quran, hadith, sunnah, iman, taqwa, spiritual development, Islamic practice, deen, worship, dhikr, tafsir, khutbah, eid
Extraction signals: spiritual content, Islamic practice guidance, Ramadan programming, worship

### 6. Justice & Civic Life (justice-civic)
Keywords: justice, civic engagement, voting, Muslim voter, policy, advocacy, Islamophobia, discrimination, prison reform, reparations, police brutality, environmental justice, housing justice, economic justice, MANA Justice
Extraction signals: justice advocacy, civic organizing, policy analysis, community empowerment

## Evaluation Criteria:
- Relevance to African-American Muslim communities (primary audience)
- Alignment with MANA's mission of community empowerment and justice
- Source credibility (prefer verified partner orgs and established Islamic publications)
- Timeliness and practical value for subscribers
- Avoid: purely political partisan content, sensationalism, content that divides Muslim communities

## Output Format:
For each article, provide:
- category: one of the six category IDs above
- relevanceScore: 0-100 (80+ = strong match, 60-79 = moderate, below 60 = weak)
- relevanceReason: 1-2 sentences explaining why this article serves MANA subscribers
- tags: 3-7 relevant keywords
- summary: 2-3 sentence summary emphasizing the community benefit`;

// ─── MOCK DISCOVERED ARTICLES (pre-seeded for demo) ──────────────────────────

const MOCK_ARTICLES: Article[] = [
  {
    id: '1',
    title: 'How Black Muslim Families Are Reclaiming Their Heritage Through Homeschooling',
    url: 'https://sapelo.org/black-muslim-homeschooling-heritage',
    source: 'Sapelo Square',
    sourceDomain: 'sapelo.org',
    summary: 'A growing movement of Black Muslim families is choosing homeschooling not just for religious reasons, but to ensure their children learn African-American Muslim history that is often absent from standard curricula.',
    category: 'history-heritage',
    relevanceScore: 94,
    relevanceReason: 'Directly addresses MANA\'s core mission of strengthening Black Muslim families and preserving heritage',
    publishedDate: '2026-03-25',
    approved: null,
    tags: ['homeschooling', 'Black Muslim', 'heritage', 'education', 'family'],
  },
  {
    id: '2',
    title: 'The Crisis of Muslim Youth Disengagement: What Masjids Can Do',
    url: 'https://muslimmatters.org/youth-disengagement-masjids',
    source: 'Muslim Matters',
    sourceDomain: 'muslimmatters.org',
    summary: 'New research shows 40% of Muslim youth aged 18-25 have reduced their masjid attendance. Community leaders share practical strategies for re-engagement through mentorship, relevant programming, and addressing real concerns.',
    category: 'youth-young-adults',
    relevanceScore: 91,
    relevanceReason: 'Highly relevant to MANA\'s youth programs and the "Each One, Reach One" mentorship framework',
    publishedDate: '2026-03-24',
    approved: null,
    tags: ['youth', 'masjid', 'engagement', 'mentorship', 'community'],
  },
  {
    id: '3',
    title: 'Imam W.D. Mohammed\'s Economic Vision: A Legacy Still Unfolding',
    url: 'https://sapelo.org/imam-wd-mohammed-economic-vision',
    source: 'Sapelo Square',
    sourceDomain: 'sapelo.org',
    summary: 'Scholars and community leaders reflect on Imam W.D. Mohammed\'s teachings on economic self-sufficiency and how his vision continues to shape African-American Muslim communities decades later.',
    category: 'legacy-profiles',
    relevanceScore: 97,
    relevanceReason: 'Directly aligned with MANA\'s historical mission and Imam Mohammed\'s legacy within the organization\'s founding context',
    publishedDate: '2026-03-22',
    approved: null,
    tags: ['Imam WD Mohammed', 'legacy', 'economics', 'African American Muslim', 'history'],
  },
  {
    id: '4',
    title: 'Muslim Voters and the 2026 Midterms: A Community Organizing Guide',
    url: 'https://icna.org/muslim-voters-2026-guide',
    source: 'ICNA',
    sourceDomain: 'icna.org',
    summary: 'A comprehensive guide for Muslim community organizers preparing for the 2026 midterm elections, including voter registration drives, candidate engagement strategies, and issue prioritization frameworks.',
    category: 'justice-civic',
    relevanceScore: 88,
    relevanceReason: 'Aligns with MANA\'s Justice & Civic Life focus and subscriber interest in civic engagement',
    publishedDate: '2026-03-21',
    approved: null,
    tags: ['voting', 'civic engagement', 'elections', 'organizing', 'Muslim voters'],
  },
  {
    id: '5',
    title: 'Ramadan Reflections: Fasting as a Tool for Social Justice Consciousness',
    url: 'https://yaqeeninstitute.org/ramadan-social-justice',
    source: 'Yaqeen Institute',
    sourceDomain: 'yaqeeninstitute.org',
    summary: 'Scholars explore how the spiritual discipline of Ramadan fasting can deepen awareness of hunger, poverty, and systemic injustice — and how communities can channel that awareness into sustained action.',
    category: 'faith-ramadan',
    relevanceScore: 85,
    relevanceReason: 'Bridges MANA\'s Faith & Ramadan and Justice & Civic Life segments with scholarly grounding',
    publishedDate: '2026-03-20',
    approved: null,
    tags: ['Ramadan', 'fasting', 'social justice', 'spirituality', 'poverty'],
  },
  {
    id: '6',
    title: 'Building Strong Muslim Marriages in the Age of Social Media',
    url: 'https://muslimmatters.org/muslim-marriages-social-media',
    source: 'Muslim Matters',
    sourceDomain: 'muslimmatters.org',
    summary: 'Marriage counselors and Islamic scholars address how social media is reshaping expectations in Muslim marriages and offer practical guidance for couples navigating digital-age challenges while maintaining Islamic values.',
    category: 'marriage-family',
    relevanceScore: 82,
    relevanceReason: 'Directly relevant to MANA\'s Marriage & Family subscriber segment',
    publishedDate: '2026-03-19',
    approved: null,
    tags: ['marriage', 'social media', 'relationships', 'Islamic values', 'counseling'],
  },
  {
    id: '7',
    title: 'The Forgotten History of Muslim Abolitionists in America',
    url: 'https://sapelo.org/muslim-abolitionists-history',
    source: 'Sapelo Square',
    sourceDomain: 'sapelo.org',
    summary: 'New historical research uncovers the significant but largely overlooked role of enslaved African Muslims in the American abolitionist movement, challenging conventional narratives about Islam and slavery.',
    category: 'history-heritage',
    relevanceScore: 96,
    relevanceReason: 'Directly serves MANA\'s History & Heritage mission and African-American Muslim identity',
    publishedDate: '2026-03-18',
    approved: null,
    tags: ['history', 'abolition', 'slavery', 'African Muslim', 'heritage'],
  },
  {
    id: '8',
    title: 'Muslim Prison Reform: Chaplains on the Front Lines of Justice',
    url: 'https://manajustice.org/prison-reform-chaplains',
    source: 'MANA Justice',
    sourceDomain: 'manajustice.org',
    summary: 'MANA-affiliated chaplains share their experiences working in correctional facilities and advocate for systemic reforms that address the disproportionate incarceration of Black and Muslim Americans.',
    category: 'justice-civic',
    relevanceScore: 99,
    relevanceReason: 'MANA\'s own content — highest relevance for justice-focused subscribers',
    publishedDate: '2026-03-17',
    approved: null,
    tags: ['prison reform', 'chaplains', 'justice', 'incarceration', 'Black Muslims'],
  },
];

// ─── NEWSLETTER HTML GENERATOR ────────────────────────────────────────────────

function generateNewsletterHTML(
  articles: Article[],
  issueNumber: number,
  selectedCategories: MANACategory[]
): string {
  const date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const approvedArticles = articles.filter(a => a.approved === true);

  const categorySections = selectedCategories.map(cat => {
    const catArticles = approvedArticles.filter(a => a.category === cat);
    if (catArticles.length === 0) return '';
    const conf = CATEGORY_CONFIG[cat];

    const articleItems = catArticles.map((article, idx) => `
      <tr>
        <td style="padding: 0 0 20px 0;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td width="42" valign="top" style="padding-right: 12px;">
                <img src="https://cdn.sender.net//email_images/294524/images/all/list_${idx + 1}.png" width="42" height="32" alt="${idx + 1}" style="display:block;" />
              </td>
              <td valign="top">
                <a href="${article.url}" style="font-family: Georgia, serif; font-size: 16px; font-weight: bold; color: #1a1a1a; text-decoration: none; line-height: 1.4;">${article.title}</a>
                <p style="font-family: Arial, sans-serif; font-size: 13px; color: #666666; margin: 6px 0 4px; line-height: 1.5;">${article.summary}</p>
                <p style="font-family: Arial, sans-serif; font-size: 12px; color: #999999; margin: 0;">
                  <em>${article.source}</em> &nbsp;·&nbsp; 
                  <a href="${article.url}" style="color: ${conf.color}; text-decoration: none;">Read more →</a>
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    `).join('');

    return `
      <!-- ${conf.label} Section -->
      <tr>
        <td style="padding: 0 0 8px 0;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0">
            <tr>
              <td style="background-color: ${conf.color}; padding: 10px 20px; border-radius: 4px 4px 0 0;">
                <p style="font-family: Arial, sans-serif; font-size: 13px; font-weight: bold; color: #ffffff; margin: 0; text-transform: uppercase; letter-spacing: 1px;">${conf.label}</p>
              </td>
            </tr>
            <tr>
              <td style="background-color: #f9f9f9; padding: 20px; border: 1px solid #eeeeee; border-top: none; border-radius: 0 0 4px 4px;">
                <table width="100%" cellpadding="0" cellspacing="0" border="0">
                  ${articleItems}
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
      <tr><td style="padding: 0 0 20px 0;"></td></tr>
    `;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>MANA Network Newsletter — Issue #${issueNumber}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f4f4f4;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          
          <!-- Header -->
          <tr>
            <td style="background-color: #1a1a1a; padding: 24px 32px; text-align: center;">
              <img src="https://manajustice.org/wp-content/uploads/2022/09/MANA-KO-Vector.svg" width="120" height="auto" alt="MANA — Muslim Alliance in North America" style="display: inline-block; margin-bottom: 12px;" />
              <p style="font-family: Georgia, serif; font-size: 22px; color: #ffffff; margin: 0 0 4px; font-weight: bold;">MANA Network Newsletter</p>
              <p style="font-family: Arial, sans-serif; font-size: 13px; color: #999999; margin: 0;">Issue #${issueNumber} &nbsp;·&nbsp; ${date}</p>
            </td>
          </tr>

          <!-- Quranic Verse Banner -->
          <tr>
            <td style="background-color: #c9a84c; padding: 14px 32px; text-align: center;">
              <p style="font-family: Georgia, serif; font-size: 14px; color: #1a1a1a; margin: 0; font-style: italic;">
                "And hold firmly to the rope of Allah all together and do not become divided." — Quran 3:103
              </p>
            </td>
          </tr>

          <!-- Intro -->
          <tr>
            <td style="padding: 28px 32px 20px;">
              <p style="font-family: Arial, sans-serif; font-size: 15px; color: #333333; line-height: 1.7; margin: 0;">
                Assalamu Alaikum wa Rahmatullahi wa Barakatuh,
              </p>
              <p style="font-family: Arial, sans-serif; font-size: 15px; color: #333333; line-height: 1.7; margin: 12px 0 0;">
                This week's digest brings you curated articles and resources aligned with your interests as part of the MANA network. These articles were written by others — we've gathered them here because they speak to the concerns and aspirations of our community.
              </p>
            </td>
          </tr>

          <!-- Article Sections -->
          <tr>
            <td style="padding: 0 32px 20px;">
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                ${categorySections}
              </table>
            </td>
          </tr>

          <!-- CTA -->
          <tr>
            <td style="padding: 0 32px 28px; text-align: center;">
              <table cellpadding="0" cellspacing="0" border="0" style="margin: 0 auto;">
                <tr>
                  <td style="background-color: #c9a84c; border-radius: 6px; padding: 12px 28px;">
                    <a href="https://manajustice.org/join" style="font-family: Arial, sans-serif; font-size: 14px; font-weight: bold; color: #1a1a1a; text-decoration: none;">Join the MANA Network →</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #1a1a1a; padding: 20px 32px; text-align: center;">
              <p style="font-family: Arial, sans-serif; font-size: 12px; color: #999999; margin: 0 0 8px;">
                Muslim Alliance in North America (MANA) &nbsp;·&nbsp; Detroit, MI &nbsp;·&nbsp; (313) 918-1911
              </p>
              <p style="font-family: Arial, sans-serif; font-size: 12px; color: #999999; margin: 0 0 8px;">
                <a href="https://manajustice.org" style="color: #c9a84c; text-decoration: none;">manajustice.org</a>
                &nbsp;·&nbsp;
                <a href="mailto:info@manajustice.org" style="color: #c9a84c; text-decoration: none;">info@manajustice.org</a>
              </p>
              <p style="font-family: Arial, sans-serif; font-size: 11px; color: #666666; margin: 0;">
                You are receiving this because you subscribed to the MANA Network newsletter.<br />
                <a href="#unsubscribe" style="color: #666666;">Unsubscribe</a> &nbsp;·&nbsp; 
                <a href="#preferences" style="color: #666666;">Update Preferences</a>
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function MANANewsletterIntelligence() {
  const [articles, setArticles] = useState<Article[]>(MOCK_ARTICLES);
  const [activeTab, setActiveTab] = useState<'discover' | 'approve' | 'compose' | 'preview'>('discover');
  const [selectedCategories, setSelectedCategories] = useState<MANACategory[]>([
    'justice-civic', 'youth-young-adults', 'history-heritage', 'faith-ramadan',
  ]);
  const [filterCategory, setFilterCategory] = useState<MANACategory | 'all'>('all');
  const [issueNumber, setIssueNumber] = useState(1);
  const [newsletterHTML, setNewsletterHTML] = useState('');
  const [crawling, setCrawling] = useState(false);
  const [crawlQuery, setCrawlQuery] = useState('');
  const [copied, setCopied] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat('ANTHROPIC', 'claude-haiku-4-5-20251001', false);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // When AI responds with discovered articles, parse and add them
  useEffect(() => {
    if (response && !isLoading && crawling) {
      setCrawling(false);
      try {
        const jsonMatch = response.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          const newArticles = JSON.parse(jsonMatch[0]) as Article[];
          const withIds = newArticles.map((a, i) => ({
            ...a,
            id: `ai-${Date.now()}-${i}`,
            approved: null as null,
          }));
          setArticles(prev => [...prev, ...withIds]);
          toast.success(`Discovered ${withIds.length} new articles`);
        }
      } catch {
        toast.error('Could not parse AI response');
      }
    }
  }, [response, isLoading, crawling]);

  const discoverArticles = useCallback(() => {
    if (!crawlQuery.trim()) return;
    setCrawling(true);

    const categoryKeywords = selectedCategories
      .flatMap(cat => CATEGORY_CONFIG[cat].keywords)
      .slice(0, 20)
      .join(', ');

    sendMessage([
      {
        role: 'system',
        content: `You are a content intelligence assistant for MANA (Muslim Alliance in North America), a Black Muslim community organization. 
Your task is to suggest relevant articles from trusted Islamic and community sources that align with MANA's mission. MANA's core focus: Black Muslim communities, justice, family strengthening, youth development, civic engagement, African-American Muslim history.
Trusted sources: ${MANA_ALIGNED_SOURCES.map(s => s.domain).join(', ')}
Return ONLY a JSON array of article objects.`,
      },
      {
        role: 'user',
        content: `Find 5-8 relevant articles for MANA's newsletter based on this topic: "${crawlQuery}"

Target categories: ${selectedCategories.map(c => CATEGORY_CONFIG[c].label).join(', ')}
Relevant keywords: ${categoryKeywords}

For each article, return:
{
  "title": "article title",
  "url": "full URL from a trusted source",
  "source": "publication name",
  "sourceDomain": "domain.com",
  "summary": "2-3 sentence summary of the article",
  "category": "one of: marriage-family, history-heritage, legacy-profiles, youth-young-adults, faith-ramadan, justice-civic",
  "relevanceScore": 0-100,
  "relevanceReason": "why this is relevant to MANA's mission",
  "publishedDate": "YYYY-MM-DD",
  "tags": ["tag1", "tag2"]
}

Return ONLY valid JSON array. Use realistic URLs from the trusted sources listed.`,
      },
    ], { max_tokens: 2000 });
  }, [crawlQuery, selectedCategories, sendMessage]);

  const approveArticle = (id: string, approved: boolean) => {
    setArticles(prev => prev.map(a => a.id === id ? { ...a, approved } : a));
  };

  const approveAll = () => {
    setArticles(prev => prev.map(a => a.approved === null ? { ...a, approved: true } : a));
  };

  const composeNewsletter = () => {
    const html = generateNewsletterHTML(articles, issueNumber, selectedCategories);
    setNewsletterHTML(html);
    setActiveTab('preview');
  };

  const copyHTML = () => {
    navigator.clipboard.writeText(newsletterHTML);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('HTML copied — paste into Constant Contact');
  };

  const approvedCount = articles.filter(a => a.approved === true).length;
  const pendingCount = articles.filter(a => a.approved === null).length;

  const filteredArticles = articles.filter(a =>
    filterCategory === 'all' || a.category === filterCategory
  );

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ background: '#060d12', minHeight: '100vh', fontFamily: '"DM Sans", sans-serif', color: '#e8e0d0' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(201,168,76,0.2)', padding: '24px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <div style={{ width: 40, height: 40, background: '#c9a84c', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#1a1a1a', fontSize: 14 }}>M</div>
              <div>
                <h1 style={{ fontFamily: '"Amiri", serif', fontSize: 26, color: '#c9a84c', margin: 0 }}>
                  MANA Newsletter Intelligence
                </h1>
                <p style={{ color: '#8a9a8a', fontSize: 12, margin: 0 }}>
                  Anthropic-powered content discovery · Curated digests by subscriber interest
                </p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ background: 'rgba(45,122,71,0.15)', border: '1px solid rgba(45,122,71,0.3)', borderRadius: 6, padding: '6px 12px', fontSize: 12, color: '#2d7a47' }}>
              {approvedCount} approved
            </div>
            <div style={{ background: 'rgba(212,168,42,0.15)', border: '1px solid rgba(212,168,42,0.3)', borderRadius: 6, padding: '6px 12px', fontSize: 12, color: '#D4A82A' }}>
              {pendingCount} pending
            </div>
            <button
              onClick={composeNewsletter}
              disabled={approvedCount === 0}
              style={{ background: approvedCount > 0 ? '#c9a84c' : 'rgba(201,168,76,0.3)', color: '#1a1a1a', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 700, cursor: approvedCount > 0 ? 'pointer' : 'not-allowed', fontSize: 14 }}
            >
              Compose Newsletter →
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0 32px' }}>
        {([
          { id: 'discover', label: 'Discover Articles' },
          { id: 'approve', label: `Review & Approve (${pendingCount})` },
          { id: 'compose', label: 'Compose' },
          { id: 'preview', label: 'Preview & Export' },
        ] as const).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: 'none', border: 'none', padding: '14px 18px', cursor: 'pointer',
              color: activeTab === tab.id ? '#c9a84c' : '#6a7a6a',
              borderBottom: activeTab === tab.id ? '2px solid #c9a84c' : '2px solid transparent',
              fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '24px 32px' }}>

        {/* ── DISCOVER TAB ── */}
        {activeTab === 'discover' && (
          <div>
            {/* Category Filter Selection */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ color: '#c9a84c', fontSize: 15, margin: '0 0 12px', fontFamily: '"Amiri", serif' }}>
                Target Subscriber Categories
              </h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(Object.entries(CATEGORY_CONFIG) as [MANACategory, typeof CATEGORY_CONFIG[MANACategory]][]).map(([cat, conf]) => {
                  const isSelected = selectedCategories.includes(cat);
                  return (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategories(prev =>
                        isSelected ? prev.filter(c => c !== cat) : [...prev, cat]
                      )}
                      style={{
                        background: isSelected ? conf.bg : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isSelected ? conf.color : 'rgba(255,255,255,0.08)'}`,
                        color: isSelected ? conf.color : '#6a7a6a',
                        padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: isSelected ? 600 : 400,
                      }}
                    >
                      {conf.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* AI Discovery */}
            <div style={{ background: '#0d1a14', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
              <h3 style={{ color: '#c9a84c', fontSize: 16, margin: '0 0 8px', fontFamily: '"Amiri", serif' }}>
                AI-Powered Article Discovery
              </h3>
              <p style={{ color: '#8a9a8a', fontSize: 13, margin: '0 0 16px', lineHeight: 1.6 }}>
                Describe a topic or theme and Claude will discover relevant articles from trusted Islamic and community sources aligned with MANA's mission.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  value={crawlQuery}
                  onChange={e => setCrawlQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && discoverArticles()}
                  placeholder="e.g. Black Muslim youth mentorship programs, Ramadan community events, prison reform advocacy..."
                  style={{ flex: 1, background: '#060d12', border: '1px solid rgba(201,168,76,0.2)', color: '#e8e0d0', padding: '10px 14px', borderRadius: 8, fontSize: 14 }}
                />
                <button
                  onClick={discoverArticles}
                  disabled={isLoading || !crawlQuery.trim()}
                  style={{ background: isLoading ? 'rgba(201,168,76,0.3)' : '#c9a84c', color: '#1a1a1a', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 700, cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: 14, whiteSpace: 'nowrap' }}
                >
                  {isLoading ? 'Discovering…' : 'Discover'}
                </button>
              </div>
              {isLoading && (
                <p style={{ color: '#8a9a8a', fontSize: 12, marginTop: 10 }}>
                  Claude is searching for relevant articles from trusted sources…
                </p>
              )}
            </div>

            {/* Source Reference */}
            <div style={{ background: '#0d1a14', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#8a9a8a', fontSize: 13, margin: '0 0 12px', fontWeight: 600 }}>TRUSTED SOURCE DOMAINS</h4>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {MANA_ALIGNED_SOURCES.map(source => (
                  <span key={source.domain} style={{ background: 'rgba(255,255,255,0.04)', color: '#6a7a6a', fontSize: 11, padding: '3px 8px', borderRadius: 4 }}>
                    {source.domain}
                  </span>
                ))}
              </div>
              <p style={{ color: '#6a7a6a', fontSize: 12, marginTop: 12, marginBottom: 0 }}>
                These domains are pre-tuned based on MANA's mission and manajustice.org content categories. Add more sources as needed.
              </p>
            </div>
          </div>
        )}

        {/* ── APPROVE TAB ── */}
        {activeTab === 'approve' && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value as MANACategory | 'all')}
                style={{ background: '#0d1a14', border: '1px solid rgba(201,168,76,0.2)', color: '#e8e0d0', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}
              >
                <option value="all">All Categories</option>
                {(Object.entries(CATEGORY_CONFIG) as [MANACategory, typeof CATEGORY_CONFIG[MANACategory]][]).map(([cat, conf]) => (
                  <option key={cat} value={cat}>{conf.label}</option>
                ))}
              </select>
              <button
                onClick={approveAll}
                style={{ background: 'rgba(45,122,71,0.15)', border: '1px solid rgba(45,122,71,0.3)', color: '#2d7a47', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
              >
                Approve All Pending
              </button>
              <span style={{ color: '#6a7a6a', fontSize: 13, marginLeft: 'auto' }}>
                {filteredArticles.length} articles
              </span>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              {filteredArticles.map(article => {
                const conf = CATEGORY_CONFIG[article.category];
                return (
                  <div
                    key={article.id}
                    style={{
                      background: '#0d1a14',
                      border: `1px solid ${article.approved === true ? 'rgba(45,122,71,0.4)' : article.approved === false ? 'rgba(201,74,74,0.3)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 10, padding: '18px 20px',
                      borderLeft: `4px solid ${conf.color}`,
                      opacity: article.approved === false ? 0.5 : 1,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ background: conf.bg, color: conf.color, fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 600 }}>
                            {conf.label}
                          </span>
                          <span style={{ background: 'rgba(255,255,255,0.05)', color: '#8a9a8a', fontSize: 11, padding: '3px 8px', borderRadius: 4 }}>
                            {article.source}
                          </span>
                          <span style={{
                            background: article.relevanceScore >= 90 ? 'rgba(45,122,71,0.15)' : article.relevanceScore >= 75 ? 'rgba(212,168,42,0.15)' : 'rgba(255,255,255,0.05)',
                            color: article.relevanceScore >= 90 ? '#2d7a47' : article.relevanceScore >= 75 ? '#D4A82A' : '#6a7a6a',
                            fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 600,
                          }}>
                            {article.relevanceScore}% match
                          </span>
                        </div>
                        <h3 style={{ fontSize: 16, color: '#e8e0d0', margin: '0 0 8px', lineHeight: 1.4 }}>
                          {article.title}
                        </h3>
                        <p style={{ color: '#8a9a8a', fontSize: 13, margin: '0 0 8px', lineHeight: 1.6 }}>
                          {article.summary}
                        </p>
                        <p style={{ color: '#6a7a6a', fontSize: 12, margin: '0 0 6px', fontStyle: 'italic' }}>
                          {article.relevanceReason}
                        </p>
                        <a href={article.url} target="_blank" rel="noopener noreferrer" style={{ color: conf.color, fontSize: 12, textDecoration: 'none' }}>
                          {article.url} ↗
                        </a>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 90 }}>
                        <button
                          onClick={() => approveArticle(article.id, true)}
                          style={{
                            background: article.approved === true ? 'rgba(45,122,71,0.3)' : 'rgba(45,122,71,0.1)',
                            border: `1px solid ${article.approved === true ? '#2d7a47' : 'rgba(45,122,71,0.3)'}`,
                            color: '#2d7a47', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: article.approved === true ? 700 : 400,
                          }}
                        >
                          {article.approved === true ? '✓ Approved' : 'Approve'}
                        </button>
                        <button
                          onClick={() => approveArticle(article.id, false)}
                          style={{
                            background: article.approved === false ? 'rgba(201,74,74,0.2)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${article.approved === false ? 'rgba(201,74,74,0.5)' : 'rgba(255,255,255,0.08)'}`,
                            color: article.approved === false ? '#c94a4a' : '#6a7a6a', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                          }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── COMPOSE TAB ── */}
        {activeTab === 'compose' && (
          <div style={{ maxWidth: 600 }}>
            <h2 style={{ fontFamily: '"Amiri", serif', fontSize: 22, color: '#c9a84c', marginBottom: 20 }}>
              Compose Newsletter
            </h2>

            <div style={{ background: '#0d1a14', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 12, padding: 24, marginBottom: 20 }}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 6, fontWeight: 600 }}>ISSUE NUMBER</label>
                <input
                  type="number"
                  value={issueNumber}
                  onChange={e => setIssueNumber(parseInt(e.target.value) || 1)}
                  style={{ background: '#060d12', border: '1px solid rgba(201,168,76,0.2)', color: '#e8e0d0', padding: '8px 14px', borderRadius: 6, fontSize: 14, width: 100 }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 8, fontWeight: 600 }}>INCLUDE CATEGORIES</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {(Object.entries(CATEGORY_CONFIG) as [MANACategory, typeof CATEGORY_CONFIG[MANACategory]][]).map(([cat, conf]) => {
                    const catApproved = articles.filter(a => a.category === cat && a.approved === true).length;
                    const isSelected = selectedCategories.includes(cat);
                    return (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategories(prev =>
                          isSelected ? prev.filter(c => c !== cat) : [...prev, cat]
                        )}
                        style={{
                          background: isSelected ? conf.bg : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${isSelected ? conf.color : 'rgba(255,255,255,0.08)'}`,
                          color: isSelected ? conf.color : '#6a7a6a',
                          padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                        }}
                      >
                        {conf.label} ({catApproved})
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
                <div style={{ fontSize: 13, color: '#2d7a47', fontWeight: 600, marginBottom: 4 }}>NEWSLETTER SUMMARY</div>
                <p style={{ color: '#8a9a8a', fontSize: 13, margin: 0 }}>
                  {approvedCount} approved articles across {selectedCategories.length} categories will be included.
                  Output will be Constant Contact-compatible HTML.
                </p>
              </div>

              <button
                onClick={composeNewsletter}
                disabled={approvedCount === 0}
                style={{ background: approvedCount > 0 ? '#c9a84c' : 'rgba(201,168,76,0.3)', color: '#1a1a1a', border: 'none', padding: '12px 28px', borderRadius: 8, fontWeight: 700, cursor: approvedCount > 0 ? 'pointer' : 'not-allowed', fontSize: 15 }}
              >
                Generate Newsletter HTML
              </button>
            </div>
          </div>
        )}

        {/* ── PREVIEW TAB ── */}
        {activeTab === 'preview' && (
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              <h2 style={{ fontFamily: '"Amiri", serif', fontSize: 22, color: '#c9a84c', margin: 0 }}>
                Newsletter Preview
              </h2>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                <button
                  onClick={copyHTML}
                  style={{ background: copied ? 'rgba(45,122,71,0.3)' : '#c9a84c', color: copied ? '#2d7a47' : '#1a1a1a', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
                >
                  {copied ? '✓ Copied!' : 'Copy HTML for Constant Contact'}
                </button>
              </div>
            </div>

            {newsletterHTML ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* Live Preview */}
                <div>
                  <div style={{ fontSize: 12, color: '#8a9a8a', marginBottom: 8, fontWeight: 600 }}>LIVE PREVIEW</div>
                  <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, overflow: 'hidden', height: 600 }}>
                    <iframe
                      srcDoc={newsletterHTML}
                      style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
                      title="Newsletter Preview"
                    />
                  </div>
                </div>
                {/* HTML Source */}
                <div>
                  <div style={{ fontSize: 12, color: '#8a9a8a', marginBottom: 8, fontWeight: 600 }}>HTML SOURCE (paste into Constant Contact)</div>
                  <textarea
                    readOnly
                    value={newsletterHTML}
                    style={{ width: '100%', height: 600, background: '#0d1a14', border: '1px solid rgba(255,255,255,0.1)', color: '#8a9a8a', padding: 16, borderRadius: 8, fontSize: 11, fontFamily: 'monospace', resize: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6a7a6a' }}>
                <p style={{ fontSize: 16, marginBottom: 16 }}>No newsletter composed yet.</p>
                <button
                  onClick={() => setActiveTab('compose')}
                  style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}
                >
                  Go to Compose →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
